import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PipeTrackCell, PipeTrackComponent } from '../../../components/pipe-track/pipe-track.component';
import { buildPipeTrackCells, buildPipeTrackSummary } from '../../../components/pipe-track/pipe-track-cells';
import {
  LivePipeline,
  finishedDuration,
  isPipelineFinished,
  pipelineCounts,
  runningWork,
  segmentKindOf,
} from '../../../models/live-pipeline.model';
import { elapsedClock } from '../../../shared/elapsed-clock';
import { TestRunLink, testRunExecutionsLink } from '../../../utils/test-run-routes';

type PipelineBadgeStatus = 'running' | 'failed' | 'passed';

interface PipelineBadge {
  status: PipelineBadgeStatus;
  label: string;
}

/**
 * Die Karte eines laufenden oder gerade beendeten Pipeline-Durchlaufs (NEX-1232),
 * selbst ausgeloest oder im Repository beobachtet (NEX-1292).
 *
 * <p>Eine Karte fuer beide Quellen, nicht zwei nebeneinander: der Unterschied liegt in
 * den Daten — die Punkte stehen fuer Tests oder Jobs, je nach `segmentKindOf`.
 *
 * <p>Der Klick fuehrt immer auf die Durchlaeufe des Testlaufs und nie zum Lauf beim Anbieter:
 * dessen Seite sieht ein Betrachter ohne Zugang zum Repository als 404. Auch ein beobachteter
 * Lauf traegt seinen Testlauf, seit der Beobachter ihn beim ersten Sehen anlegt; bleibt die
 * Kennung trotzdem leer, ist die Karte lieber stumm als irrefuehrend.
 *
 * <p>Die Leiste entsteht aus den Punkten der Antwort; die Ableitung teilt sich die
 * Karte mit dem Live-Band der Testlauf-Uebersicht (`buildPipeTrackCells`).
 *
 * <p>Ein laufender Durchlauf bleibt blau, auch wenn schon ein Test fehlgeschlagen ist: das
 * verlangt das Design, und der Fehler bleibt ueber den Zaehler „{n} rot“ und die roten
 * Kaestchen sichtbar. Rot wird der Rahmen erst, wenn der Lauf nicht bestanden endet — ob
 * ROT oder GRUEN, sagt sein Ergebnis, nicht die Zahl roter Kaestchen: ein abgebrochener
 * Lauf ist rot, auch ohne fehlgeschlagenen Test.
 */
@Component({
  selector: 'app-live-pipe-card',
  imports: [NgTemplateOutlet, RouterLink, MatIcon, TranslatePipe, PipeTrackComponent],
  templateUrl: './live-pipe-card.component.html',
  styleUrl: './live-pipe-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LivePipeCardComponent {
  private readonly translate = inject(TranslateService);

  readonly pipeline = input.required<LivePipeline>();

  protected readonly counts = computed(() => pipelineCounts(this.pipeline()));

  protected readonly running = computed(() => this.pipeline().status === 'RUNNING');

  protected readonly hasFailure = computed(() => this.counts().failed > 0);

  protected readonly failed = computed(
    () => isPipelineFinished(this.pipeline()) && this.pipeline().status !== 'PASSED',
  );

  /** Die Klassen der Karte an einer Stelle: Link und stumme Karte tragen dieselben. */
  protected readonly cardClass = computed(() =>
    ['live-pipe-card', this.running() ? 'live-pipe-card--running' : '', this.failed() ? 'live-pipe-card--failed' : '']
      .filter(Boolean)
      .join(' '),
  );

  /**
   * Der Absprung in die Durchlaeufe des Testlaufs; {@code null}, solange die Karte keinen kennt.
   */
  protected readonly runLink = computed<TestRunLink | null>(() => {
    const entry = this.pipeline();
    return entry.runId ? testRunExecutionsLink(entry.projectId, entry.runId) : null;
  });

  protected readonly badge = computed<PipelineBadge>(() => {
    if (this.running()) {
      return { status: 'running', label: 'liveDashboard.livePipeline.status.running' };
    }
    return this.failed()
      ? { status: 'failed', label: 'liveDashboard.livePipeline.status.red' }
      : { status: 'passed', label: 'liveDashboard.livePipeline.status.green' };
  });

  protected readonly segmentKind = computed(() => segmentKindOf(this.pipeline()));

  /**
   * Ohne Namen keine Zeile — so setzt es das Design; die Position allein sagt nichts.
   * Meldet die Pipeline ihre Tests ausdruecklich, bleibt die Zeile zwischen zwei Tests
   * stehen: sonst sprang die Karte bei jedem Testwechsel zwischen Zaehler und Herkunft hin
   * und her. Bewusst zaehlt hier nur das gelieferte Feld, nicht `segmentKindOf`: ein
   * ausgeloester Lauf ohne das Feld blendet die Zeile ohne Namen weiter aus.
   */
  protected readonly current = computed(() => {
    const entry = this.pipeline();
    const work = runningWork(entry);
    return work && (work.name || entry.segmentKind === 'TESTS') ? work : null;
  });

  protected readonly currentLabelKey = computed(() =>
    this.segmentKind() === 'JOBS' ? 'liveDashboard.livePipeline.currentJob' : 'liveDashboard.livePipeline.currentTest',
  );

  protected readonly cells = computed<PipeTrackCell[]>(() => buildPipeTrackCells(this.pipeline(), this.translate));

  protected readonly trackLabel = computed(() =>
    buildPipeTrackSummary(this.cells(), this.segmentKind(), this.translate),
  );

  /** Die Laufzeit des Durchlaufs; {@code null}, sobald er nicht mehr laeuft. */
  protected readonly elapsed = elapsedClock(() => (this.running() ? this.pipeline().startedAt : null));

  protected readonly duration = computed(() => finishedDuration(this.pipeline()));
}
